import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import { API_URL } from "../services/api";
import { FileXls, FileDoc, Flask, FilePdf, BookOpen, SpinnerGap, type Icon as PhosphorIcon } from "@phosphor-icons/react";

// ============================================================
// Report Center — Multi-format data export
// ============================================================

interface ExportFormat {
  id: string;
  label: string;
  description: string;
  IconComponent: PhosphorIcon;
  extension: string;
  color: string;
  hasLangSelector?: boolean;
}

const TEXTS = {
    es: {
        backToProject: '← Volver al Proyecto',
        reportCenter: 'Centro de Reportes',
        exportSubtitle: 'Exporta los resultados del análisis en múltiples formatos.',
        generating: 'Generando...',
        download: 'Descargar',
        downloadSuccess: 'descargado correctamente',
        downloadFailed: 'Descarga fallida',
        ipNotice: 'Los datos exportados son resultados calculados. La metodología DACAMS, fórmulas y algoritmos son propiedad intelectual de Aipron Consulting y no se incluyen en las exportaciones.',
        fmtExcelDesc: 'Libro multi-hoja con KPIs, perfiles horarios y asignaciones de vuelos. Listo para Tableau / Power BI.',
        fmtCsvDesc: 'Tabla de KPIs ligera. Formato universal para cualquier herramienta de datos.',
        fmtNotebookDesc: 'Datos de análisis precargados con celdas Python listas para ejecutar. Para científicos de datos.',
        fmtPdfDesc: 'Informe APCAP completo con gráficos y resumen ejecutivo. Requiere renderizado del servidor.',
        fmtApcapDesc: 'Documento Word profesional de consultoría con análisis de capacidad, demanda y dotación conforme a OACI Doc 9476. Generado con IA.',
        fmtApcapLabel: 'APCAP Consulting Report',
    },
    en: {
        backToProject: '← Back to Project',
        reportCenter: 'Report Center',
        exportSubtitle: 'Export analysis results in multiple formats.',
        generating: 'Generating...',
        download: 'Download',
        downloadSuccess: 'downloaded successfully',
        downloadFailed: 'Download failed',
        ipNotice: 'Exported data contains computed results only. The DACAMS methodology, formulas, and algorithms are the intellectual property of Aipron Consulting and are not included in exports.',
        fmtExcelDesc: 'Multi-sheet workbook with KPIs, hourly profiles, and flight allocations. Ready for Tableau / Power BI.',
        fmtCsvDesc: 'Lightweight KPI table. Universal format for any data tool.',
        fmtNotebookDesc: 'Pre-loaded analysis data with ready-to-run Python cells. For data scientists to validate and explore.',
        fmtPdfDesc: 'Full APCAP report with charts and executive summary. Requires server-side rendering.',
        fmtApcapDesc: 'Professional consulting Word document with capacity, demand and staffing analysis per ICAO Doc 9476. AI-generated.',
        fmtApcapLabel: 'APCAP Consulting Report',
    },
};

const ReportPage: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const tx = TEXTS[(language as 'es' | 'en') || 'en'] || TEXTS.en;

  const EXPORT_FORMATS: ExportFormat[] = [
    {
      id: "excel",
      label: "Excel (.xlsx)",
      description: tx.fmtExcelDesc,
      IconComponent: FileXls,
      extension: "xlsx",
      color: "#217346",
    },
    {
      id: "csv",
      label: "CSV (.csv)",
      description: tx.fmtCsvDesc,
      IconComponent: FileDoc,
      extension: "csv",
      color: "#4B8BBE",
    },
    {
      id: "notebook",
      label: "Jupyter Notebook (.ipynb)",
      description: tx.fmtNotebookDesc,
      IconComponent: Flask,
      extension: "ipynb",
      color: "#F37726",
    },
    {
      id: "pdf",
      label: "PDF Report",
      description: tx.fmtPdfDesc,
      IconComponent: FilePdf,
      extension: "pdf",
      color: "#D32F2F",
    },
    {
      id: "apcap",
      label: tx.fmtApcapLabel,
      description: tx.fmtApcapDesc,
      IconComponent: BookOpen,
      extension: "docx",
      color: "#F26430",
      hasLangSelector: true,
    },
  ];

  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>("");
  const [apcapLang, setApcapLang] = useState<'es' | 'en'>('es');

  const isDark = theme === "dark";

  // Fetch project name
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${API_URL}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setProjectName(data.name || "Project");
        }
      } catch {
        // Ignore
      }
    };
    if (projectId) fetchProject();
  }, [projectId]);

  const handleDownload = async (format: ExportFormat) => {
    setDownloading(format.id);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem("access_token");
      const langParam = format.id === 'apcap' ? `&language=${apcapLang}` : '';
      const response = await fetch(
        `${API_URL}/projects/${projectId}/report?format=${format.id}${langParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(err.detail || `Export failed: ${response.statusText}`);
      }

      // Download the file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DACAMS_${projectName || "Report"}_${new Date().toISOString().slice(0, 10)}.${format.extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess(`${format.label} ${tx.downloadSuccess}`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: any) {
      setError(e.message || tx.downloadFailed);
    } finally {
      setDownloading(null);
    }
  };

  const styles = {
    page: {
      minHeight: "100vh",
      backgroundColor: isDark ? "#1C1C1C" : "var(--bg)",
      color: isDark ? "#F9FAFB" : "#111827",
      padding: "32px",
    } as React.CSSProperties,
    header: {
      maxWidth: 900,
      margin: "0 auto 32px",
    } as React.CSSProperties,
    backLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 14,
      color: isDark ? "#9CA3AF" : "#6B7280",
      textDecoration: "none",
      marginBottom: 12,
    } as React.CSSProperties,
    title: {
      fontSize: 28,
      fontWeight: 700,
      margin: "0 0 8px",
    } as React.CSSProperties,
    subtitle: {
      fontSize: 16,
      color: isDark ? "#9CA3AF" : "#6B7280",
      margin: 0,
    } as React.CSSProperties,
    grid: {
      maxWidth: 900,
      margin: "0 auto",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
      gap: 20,
    } as React.CSSProperties,
    card: (color: string, isDownloading: boolean) => ({
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#E5E7EB"}`,
      borderRadius: 16,
      padding: "28px 24px",
      display: "flex",
      flexDirection: "column" as const,
      gap: 16,
      transition: "all 0.2s ease",
      cursor: isDownloading ? "wait" : "pointer",
      opacity: isDownloading ? 0.6 : 1,
      borderLeft: `4px solid ${color}`,
    }),
    cardIcon: {
      fontSize: 36,
    } as React.CSSProperties,
    cardTitle: {
      fontSize: 18,
      fontWeight: 600,
      margin: 0,
    } as React.CSSProperties,
    cardDesc: {
      fontSize: 14,
      color: isDark ? "#9CA3AF" : "#6B7280",
      lineHeight: 1.5,
      margin: 0,
    } as React.CSSProperties,
    downloadBtn: (color: string) => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "10px 20px",
      backgroundColor: color,
      color: "#FFFFFF",
      border: "none",
      borderRadius: 10,
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      transition: "opacity 0.15s",
      marginTop: "auto",
    }),
    alert: (type: "error" | "success") => ({
      maxWidth: 900,
      margin: "0 auto 20px",
      padding: "14px 20px",
      borderRadius: 12,
      fontSize: 14,
      fontWeight: 500,
      backgroundColor: type === "error"
        ? "rgba(239, 68, 68, 0.12)"
        : "rgba(16, 185, 129, 0.12)",
      color: type === "error" ? "#EF4444" : "#10B981",
      border: `1px solid ${type === "error" ? "rgba(239, 68, 68, 0.3)" : "rgba(16, 185, 129, 0.3)"}`,
    }),
    ipNotice: {
      maxWidth: 900,
      margin: "32px auto 0",
      padding: "16px 20px",
      borderRadius: 12,
      backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F9FAFB",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#E5E7EB"}`,
      fontSize: 12,
      color: isDark ? "#6B7280" : "#9CA3AF",
      lineHeight: 1.6,
    } as React.CSSProperties,
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <Link to={`/projects/${projectId}`} style={styles.backLink}>
          {tx.backToProject}
        </Link>
        <h1 style={styles.title}>
          {tx.reportCenter}
        </h1>
        <p style={styles.subtitle}>
          {projectName && <strong>{projectName}</strong>}
          {projectName && " — "}
          {tx.exportSubtitle}
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={styles.alert("error")}>⚠️ {error}</div>
      )}
      {success && (
        <div style={styles.alert("success")}>✓ {success}</div>
      )}

      {/* Export Cards */}
      <div style={styles.grid}>
        {EXPORT_FORMATS.map((fmt) => (
          <div
            key={fmt.id}
            style={styles.card(fmt.color, downloading === fmt.id)}
          >
            <fmt.IconComponent size={32} color={fmt.color} weight="bold" />
            <h3 style={styles.cardTitle}>{fmt.label}</h3>
            <p style={styles.cardDesc}>{fmt.description}</p>
            {fmt.hasLangSelector && (
              <div style={{ display: 'flex', gap: 8, margin: '8px 0' }}>
                {(['es', 'en'] as const).map(l => (
                  <button
                    key={l}
                    onClick={() => setApcapLang(l)}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      border: `2px solid ${apcapLang === l ? fmt.color : isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB'}`,
                      backgroundColor: apcapLang === l ? `${fmt.color}18` : 'transparent',
                      color: apcapLang === l ? fmt.color : isDark ? '#9CA3AF' : '#6B7280',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {l === 'es' ? '🇪🇸 Español' : '🇬🇧 English'}
                  </button>
                ))}
              </div>
            )}
            <button
              style={styles.downloadBtn(fmt.color)}
              onClick={() => handleDownload(fmt)}
              disabled={downloading !== null}
            >
              {downloading === fmt.id ? (
                <>
                  <SpinnerGap size={16} weight="bold" style={{ animation: "spin 1s linear infinite" }} />
                  {tx.generating}
                </>
              ) : (
                <>
                  ↓ {tx.download} .{fmt.extension}
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* IP Notice */}
      <div style={styles.ipNotice}>
        <strong>© Aipron Consulting</strong> — {tx.ipNotice}
      </div>
    </div>
  );
};

export default ReportPage;